import { apiClient } from '../lib/apiClient'
import {
  Customer, Project, Resource, ResourceAllocation, TimesheetEntry,
  ResourceRoleType, Holiday, Partner, PartnerRateSheet, PartnerRateSheetEntry, CurrencyRate,
  TimeOffRequest, OrgManagerMember
} from '../types'

class DataService {
  async getCustomers(orgId: string, filters?: { name?: string; industry?: string; region?: string }): Promise<Customer[]> {
    const params = new URLSearchParams()
    if (filters?.name) params.set('name', filters.name)
    if (filters?.industry) params.set('industry', filters.industry)
    if (filters?.region) params.set('region', filters.region)
    const qs = params.toString()
    return apiClient.get<Customer[]>(`/orgs/${orgId}/customers${qs ? `?${qs}` : ''}`)
  }

  async createCustomer(orgId: string, customer: Omit<Customer, 'id' | 'org_id'>): Promise<Customer> {
    return apiClient.post<Customer>(`/orgs/${orgId}/customers`, customer)
  }

  async updateCustomer(id: string, updates: Partial<Customer>): Promise<Customer> {
    return apiClient.patch<Customer>(`/customers/${id}`, updates)
  }

  async deleteCustomer(id: string): Promise<void> {
    return apiClient.delete(`/customers/${id}`)
  }

  async getProjects(orgId: string, filters?: { name?: string; status?: string; customerId?: string; startDate?: string; endDate?: string }): Promise<Project[]> {
    const params = new URLSearchParams()
    if (filters?.name) params.set('name', filters.name)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.customerId) params.set('customerId', filters.customerId)
    if (filters?.startDate) params.set('startDate', filters.startDate)
    if (filters?.endDate) params.set('endDate', filters.endDate)
    const qs = params.toString()
    return apiClient.get<Project[]>(`/orgs/${orgId}/projects${qs ? `?${qs}` : ''}`)
  }

  async createProject(orgId: string, project: Omit<Project, 'id' | 'org_id'>): Promise<Project> {
    return apiClient.post<Project>(`/orgs/${orgId}/projects`, project)
  }

  async updateProject(id: string, updates: Partial<Project>): Promise<Project> {
    return apiClient.patch<Project>(`/projects/${id}`, updates)
  }

  async deleteProject(id: string): Promise<void> {
    return apiClient.delete(`/projects/${id}`)
  }

  async getResources(orgId: string): Promise<Resource[]> {
    return apiClient.get<Resource[]>(`/orgs/${orgId}/resources`)
  }

  async createResource(orgId: string, resource: Omit<Resource, 'id' | 'org_id'>): Promise<Resource> {
    return apiClient.post<Resource>(`/orgs/${orgId}/resources`, resource)
  }

  async updateResource(id: string, updates: Partial<Resource>): Promise<Resource> {
    return apiClient.patch<Resource>(`/resources/${id}`, updates)
  }

  async deleteResource(id: string): Promise<void> {
    return apiClient.delete(`/resources/${id}`)
  }

  async getAllocations(orgId: string, filters?: { projectId?: string; resourceId?: string; roleTypeId?: string; startDate?: string; endDate?: string }): Promise<ResourceAllocation[]> {
    const params = new URLSearchParams()
    if (filters?.projectId) params.set('projectId', filters.projectId)
    if (filters?.resourceId) params.set('resourceId', filters.resourceId)
    if (filters?.roleTypeId) params.set('roleTypeId', filters.roleTypeId)
    if (filters?.startDate) params.set('startDate', filters.startDate)
    if (filters?.endDate) params.set('endDate', filters.endDate)
    const qs = params.toString()
    return apiClient.get<ResourceAllocation[]>(`/orgs/${orgId}/allocations${qs ? `?${qs}` : ''}`)
  }

  async createAllocation(orgId: string, allocation: Omit<ResourceAllocation, 'id' | 'org_id'>): Promise<ResourceAllocation> {
    return apiClient.post<ResourceAllocation>(`/orgs/${orgId}/allocations`, allocation)
  }

  async updateAllocation(id: string, updates: Partial<ResourceAllocation>): Promise<ResourceAllocation> {
    return apiClient.patch<ResourceAllocation>(`/allocations/${id}`, updates)
  }

  async deleteAllocation(id: string): Promise<void> {
    return apiClient.delete(`/allocations/${id}`)
  }

  async getTimesheets(orgId: string): Promise<TimesheetEntry[]> {
    return apiClient.get<TimesheetEntry[]>(`/orgs/${orgId}/timesheet-entries`)
  }

  async createTimesheet(orgId: string, ts: Omit<TimesheetEntry, 'id' | 'org_id'>): Promise<TimesheetEntry> {
    return apiClient.post<TimesheetEntry>(`/orgs/${orgId}/timesheet-entries`, ts)
  }

  async updateTimesheet(id: string, updates: Partial<TimesheetEntry>): Promise<TimesheetEntry> {
    return apiClient.patch<TimesheetEntry>(`/timesheet-entries/${id}`, updates)
  }

  async deleteTimesheet(id: string): Promise<void> {
    return apiClient.delete(`/timesheet-entries/${id}`)
  }

  async getResourceRoleTypes(orgId: string): Promise<ResourceRoleType[]> {
    return apiClient.get<ResourceRoleType[]>(`/orgs/${orgId}/role-types`)
  }

  async createResourceRoleType(orgId: string, roleType: Omit<ResourceRoleType, 'id' | 'org_id'>): Promise<ResourceRoleType> {
    return apiClient.post<ResourceRoleType>(`/orgs/${orgId}/role-types`, roleType)
  }

  async updateResourceRoleType(id: string, updates: Partial<ResourceRoleType>): Promise<ResourceRoleType> {
    return apiClient.patch<ResourceRoleType>(`/role-types/${id}`, updates)
  }

  async deleteResourceRoleType(id: string): Promise<void> {
    return apiClient.delete(`/role-types/${id}`)
  }

  async getHolidays(orgId: string): Promise<Holiday[]> {
    return apiClient.get<Holiday[]>(`/orgs/${orgId}/holidays`)
  }

  async createHoliday(orgId: string, holiday: Omit<Holiday, 'id' | 'org_id'>): Promise<Holiday> {
    return apiClient.post<Holiday>(`/orgs/${orgId}/holidays`, holiday)
  }

  async updateHoliday(id: string, updates: Partial<Holiday>): Promise<Holiday> {
    return apiClient.patch<Holiday>(`/holidays/${id}`, updates)
  }

  async deleteHoliday(id: string): Promise<void> {
    return apiClient.delete(`/holidays/${id}`)
  }

  async getPartners(orgId: string): Promise<Partner[]> {
    return apiClient.get<Partner[]>(`/orgs/${orgId}/partners`)
  }

  async createPartner(orgId: string, partner: Omit<Partner, 'id' | 'org_id'>): Promise<Partner> {
    return apiClient.post<Partner>(`/orgs/${orgId}/partners`, partner)
  }

  async updatePartner(id: string, updates: Partial<Partner>): Promise<Partner> {
    return apiClient.patch<Partner>(`/partners/${id}`, updates)
  }

  async deletePartner(id: string): Promise<void> {
    return apiClient.delete(`/partners/${id}`)
  }

  async getPartnerRateSheets(orgId: string): Promise<PartnerRateSheet[]> {
    return apiClient.get<PartnerRateSheet[]>(`/orgs/${orgId}/partner-rate-sheets`)
  }

  async createPartnerRateSheet(orgId: string, sheet: Omit<PartnerRateSheet, 'id' | 'org_id'>): Promise<PartnerRateSheet> {
    return apiClient.post<PartnerRateSheet>(`/orgs/${orgId}/partner-rate-sheets`, sheet)
  }

  async updatePartnerRateSheet(id: string, updates: Partial<PartnerRateSheet>): Promise<PartnerRateSheet> {
    return apiClient.patch<PartnerRateSheet>(`/partner-rate-sheets/${id}`, updates)
  }

  async deletePartnerRateSheet(id: string): Promise<void> {
    return apiClient.delete(`/partner-rate-sheets/${id}`)
  }

  async getPartnerRateSheetEntries(orgId: string): Promise<PartnerRateSheetEntry[]> {
    return apiClient.get<PartnerRateSheetEntry[]>(`/orgs/${orgId}/partner-rate-sheet-entries`)
  }

  async createPartnerRateSheetEntry(orgId: string, entry: Omit<PartnerRateSheetEntry, 'id' | 'org_id'>): Promise<PartnerRateSheetEntry> {
    return apiClient.post<PartnerRateSheetEntry>(`/orgs/${orgId}/partner-rate-sheet-entries`, entry)
  }

  async updatePartnerRateSheetEntry(id: string, updates: Partial<PartnerRateSheetEntry>): Promise<PartnerRateSheetEntry> {
    return apiClient.patch<PartnerRateSheetEntry>(`/partner-rate-sheet-entries/${id}`, updates)
  }

  async deletePartnerRateSheetEntry(id: string): Promise<void> {
    return apiClient.delete(`/partner-rate-sheet-entries/${id}`)
  }

  async getExchangeRates(orgId: string): Promise<CurrencyRate[]> {
    return apiClient.get<CurrencyRate[]>(`/orgs/${orgId}/currency-rates`)
  }

  async createExchangeRate(orgId: string, rate: Omit<CurrencyRate, 'id' | 'org_id'>): Promise<CurrencyRate> {
    return apiClient.post<CurrencyRate>(`/orgs/${orgId}/currency-rates`, rate)
  }

  async updateExchangeRate(id: string, updates: { rate: number }): Promise<CurrencyRate> {
    return apiClient.patch<CurrencyRate>(`/currency-rates/${id}`, updates)
  }

  async getOrgManagers(orgId: string): Promise<OrgManagerMember[]> {
    return apiClient.get<OrgManagerMember[]>(`/orgs/${orgId}/managers`)
  }

  async getTimeOffRequests(orgId: string): Promise<TimeOffRequest[]> {
    return apiClient.get<TimeOffRequest[]>(`/orgs/${orgId}/time-off-requests`)
  }

  async createTimeOffRequest(orgId: string, request: Omit<TimeOffRequest, 'id' | 'org_id' | 'created_at'>): Promise<TimeOffRequest> {
    return apiClient.post<TimeOffRequest>(`/orgs/${orgId}/time-off-requests`, request)
  }

  async updateTimeOffRequest(id: string, updates: Partial<TimeOffRequest>): Promise<TimeOffRequest> {
    debugger;
    return apiClient.patch<TimeOffRequest>(`/time-off-requests/${id}`, updates)
  }

  async deleteTimeOffRequest(id: string): Promise<void> {
    return apiClient.delete(`/time-off-requests/${id}`)
  }

  async getEffectiveRates(orgId: string, projectId: string, resourceRoleTypeId: string): Promise<{
    costRate: number; billingRate: number; currency: 'USD' | 'GBP' | 'INR'; isPartnerRateMissing: boolean
  }> {
    return apiClient.get(`/orgs/${orgId}/effective-rates?projectId=${projectId}&roleTypeId=${resourceRoleTypeId}`)
  }
}

export const dataService = new DataService()
